import { LanguageCode } from '@heidi/schema/ml-scribe';
import {
  mapLocaleAliasToSupportedLocale,
  type MapLocaleToSupportedOptions,
  type SupportedLocale,
} from './supported-locales';

export type DisplayLanguageDefinition = {
  code: LanguageCode;
  is_rtl: boolean;
};

/**
 * Supported Heidi UI display languages and their text direction.
 * Add new entries here (do not maintain a separate `LanguageCode[]`).
 */
export const display_language_definitions: readonly DisplayLanguageDefinition[] = [
  { code: LanguageCode.en, is_rtl: false },
  { code: LanguageCode.de, is_rtl: false },
  { code: LanguageCode.fr, is_rtl: false },
  { code: LanguageCode.es, is_rtl: false },
  { code: LanguageCode.it, is_rtl: false },
  { code: LanguageCode.zh, is_rtl: false },
  { code: LanguageCode.zh_Hant, is_rtl: false },
  { code: LanguageCode.ja, is_rtl: false },
  { code: LanguageCode.ko, is_rtl: false },
  { code: LanguageCode.ca, is_rtl: false },
  { code: LanguageCode.da, is_rtl: false },
  { code: LanguageCode.nl, is_rtl: false },
  { code: LanguageCode.fi, is_rtl: false },
  { code: LanguageCode.no, is_rtl: false },
  { code: LanguageCode.pl, is_rtl: false },
  { code: LanguageCode.pt, is_rtl: false },
  { code: LanguageCode.sv, is_rtl: false },
  { code: LanguageCode.ar_SA, is_rtl: true },
];

export const supported_display_languages: LanguageCode[] =
  display_language_definitions.map((d) => d.code);

export const experimental_display_languages: LanguageCode[] = [];

/**
 * Full list of Heidi display `LanguageCode` values (including Arabic).
 *
 * Do **not** use this alone for account/settings language pickers: Arabic is only included via
 * the `useSelectableDisplayLanguageCodes` hook when the RTL feature flag is enabled.
 */
export const languages = supported_display_languages;

/** Shared list logic for {@link getSelectableDisplayLanguageCodes} and {@link useSelectableDisplayLanguageCodes}. */
export const buildSelectableDisplayLanguageCodes = (
  isRtlLocaleFeatureEnabled: boolean
): LanguageCode[] =>
  isRtlLocaleFeatureEnabled
    ? supported_display_languages
    : supported_display_languages.filter((code) => code !== LanguageCode.ar_SA);

/**
 * Display languages for sync pickers and country filters (never includes Arabic).
 * Prefer {@link useSelectableDisplayLanguageCodes} in React so Arabic respects the client flag.
 */
export const getSelectableDisplayLanguageCodes = (): LanguageCode[] =>
  buildSelectableDisplayLanguageCodes(false);

export const default_display_language: LanguageCode = LanguageCode.en;

const RTL_DISPLAY_LANGUAGE_CODES = new Set<LanguageCode>(
  display_language_definitions.filter((d) => d.is_rtl).map((d) => d.code)
);
RTL_DISPLAY_LANGUAGE_CODES.add(LanguageCode.ar_AE);

const RTL_SUPPORTED_LOCALES = new Set<SupportedLocale>();
for (const def of display_language_definitions) {
  if (!def.is_rtl) continue;
  const mapped = mapLocaleAliasToSupportedLocale(def.code, {
    isRtlLocaleFeatureEnabled: true,
  });
  if (mapped !== undefined) {
    RTL_SUPPORTED_LOCALES.add(mapped);
  }
}

/**
 * Whether a **schema** `LanguageCode` is marked RTL in definitions (static).
 *
 * For document / router locale strings, use {@link isRtlAppLocale} or
 * {@link getDocumentTextDirection} with the same client RTL flag as locale resolution.
 */
export function isRtlDisplayLanguage(code: LanguageCode | null | undefined): boolean {
  if (code === null || code === undefined) {
    return false;
  }
  return RTL_DISPLAY_LANGUAGE_CODES.has(code);
}

export function isRtlAppLocale(
  locale: string | null | undefined,
  options?: MapLocaleToSupportedOptions
): boolean {
  const resolved = mapLocaleAliasToSupportedLocale(locale, options);
  return resolved !== undefined && RTL_SUPPORTED_LOCALES.has(resolved);
}

export function getDocumentTextDirection(
  locale: string | null | undefined,
  options?: MapLocaleToSupportedOptions
): 'rtl' | 'ltr' {
  return isRtlAppLocale(locale, options) ? 'rtl' : 'ltr';
}
